import { useState } from "react";
import { Dropdown, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import AlertModal from "components/AlertModal";
import * as C from "components/EntityCard/styles";
import ModalComponent from "components/ModalComponent";
import { useCompany } from "providers/Company";
import { api } from "services";
import { Company } from "types/company";
import { cnpjMask, mobilePhoneMask } from "utils/formMasks";
import { toast } from "utils/toast";

import CompanyEditForm from "../Forms/CompanyEditForm";

interface CompanyCardProps {
  company: Company;
  isActive?: boolean;
}

const getInitials = (name: string) => {
  const parts = name.trim().split(" ");
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const CompanyCard = ({ company, isActive = true }: CompanyCardProps) => {
  const navigate = useNavigate();
  const [companyEditModalShow, setCompanyEditModalShow] = useState(false);
  const [alertModalShow, setAlertModalShow] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    setInactiveCompaniesCurrentPage,
    setActiveCompaniesCurrentPage,
    getInactiveCompanies,
    getActiveCompanies,
  } = useCompany();

  const handleActivateCompany = async () => {
    setIsLoading(true);
    try {
      await api.patch(`/Company/${company.id}/${isActive ? "disable" : "enable"}`);
      setIsLoading(false);
      toast.fire({ icon: "success", title: `Instituição ${isActive ? "des" : ""}ativada com sucesso!` });
      setInactiveCompaniesCurrentPage(1);
      setActiveCompaniesCurrentPage(1);
      getInactiveCompanies();
      getActiveCompanies();
      setAlertModalShow(false);
    } catch {
      setIsLoading(false);
      toast.fire({ icon: "error", title: "Ocorreu um erro, tente novamente." });
    }
  };

  const phone = mobilePhoneMask(`${company.dddMobilePhone}${company.mobilePhone}`);
  const address = company.addressCEP
    ? `${company.addressRoad}, ${company.addressNumber}, ${company.addressCity} - ${company.addressState}`
    : null;

  return (
    <>
      <C.Card
        style={{ "--left-color": isActive ? "var(--primary)" : "var(--gray)" } as React.CSSProperties}
        aria-label={`Instituição: ${company.name}`}
      >
        <C.CardHeader>
          <C.AvatarCircle aria-hidden="true">{getInitials(company.name)}</C.AvatarCircle>

          <OverlayTrigger placement="top" overlay={<Tooltip>{company.name}</Tooltip>}>
            <C.EntityName>{company.name}</C.EntityName>
          </OverlayTrigger>

          <Dropdown align="end">
            <Dropdown.Toggle as="div" bsPrefix="no-caret">
              <C.ActionButton aria-label={`Ações para ${company.name}`} aria-haspopup="true">
                <i className="uil uil-ellipsis-v" aria-hidden="true" />
              </C.ActionButton>
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item as="button" onClick={() => setCompanyEditModalShow(true)}>
                <i className="uil uil-edit-alt me-2" aria-hidden="true" />
                Editar
              </Dropdown.Item>
              {isActive && (
                <Dropdown.Item
                  as="button"
                  onClick={() => navigate("/instituicoes/instituicao-profissionais", { state: { company } })}
                >
                  <i className="uil uil-medkit me-2" aria-hidden="true" />
                  Profissionais
                </Dropdown.Item>
              )}
              <Dropdown.Divider />
              <Dropdown.Item
                as="button"
                className={isActive ? "text-danger" : "text-success"}
                onClick={() => setAlertModalShow(true)}
              >
                <i className={`uil uil-${isActive ? "times" : "check"}-circle me-2`} aria-hidden="true" />
                {isActive ? "Desativar" : "Ativar"}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </C.CardHeader>

        <C.CardBody aria-label="Dados da instituição">
          <C.MetaItem>
            <C.MetaLabel>CNPJ</C.MetaLabel>
            <C.MetaValue>{cnpjMask(company.cnpj)}</C.MetaValue>
          </C.MetaItem>

          <C.MetaItem>
            <C.MetaLabel>Contato</C.MetaLabel>
            <C.MetaValueStack>
              <span>{company.contactName}</span>
              {company.contactJobPosition && <small>{company.contactJobPosition}</small>}
            </C.MetaValueStack>
          </C.MetaItem>

          <C.MetaItem>
            <C.MetaLabel>E-mail / Tel.</C.MetaLabel>
            <C.MetaValueStack>
              <OverlayTrigger placement="top" overlay={<Tooltip>{company.email}</Tooltip>}>
                <span>{company.email || "—"}</span>
              </OverlayTrigger>
              {phone && <small>{phone}</small>}
            </C.MetaValueStack>
          </C.MetaItem>

          <C.MetaItem>
            <C.MetaLabel>Endereço</C.MetaLabel>
            {address ? (
              <OverlayTrigger placement="top" overlay={<Tooltip>{address}</Tooltip>}>
                <C.MetaValue>{address}</C.MetaValue>
              </OverlayTrigger>
            ) : (
              <C.MetaValueMuted>Não informado</C.MetaValueMuted>
            )}
          </C.MetaItem>
        </C.CardBody>
      </C.Card>

      <AlertModal
        show={alertModalShow}
        onHide={() => setAlertModalShow(false)}
        title={`Tem certeza que deseja ${isActive ? "des" : ""}ativar essa instituição?`}
        onClickButton={handleActivateCompany}
        btnVariant={isActive ? "danger" : "success"}
        isLoading={isLoading}
      />
      <ModalComponent
        title="Editar Instituição"
        show={companyEditModalShow}
        onHide={() => setCompanyEditModalShow(false)}
        size="xl"
      >
        <CompanyEditForm onHide={() => setCompanyEditModalShow(false)} company={company} />
      </ModalComponent>
    </>
  );
};

export default CompanyCard;
